const TOKEN_KEY = "velo_admin_token";
const API_KEY = "velo_admin_api";
const OP_KEY = "velo_admin_op";

export function getApiBase(): string {
  const fromEnv = (import.meta.env.VITE_LICENSE_API || "").replace(/\/+$/, "");
  const saved = (localStorage.getItem(API_KEY) || "").replace(/\/+$/, "");
  return saved || fromEnv || "http://127.0.0.1:8787";
}

export function setApiBase(url: string) {
  localStorage.setItem(API_KEY, url.replace(/\/+$/, ""));
}

export function getToken(): string {
  return sessionStorage.getItem(TOKEN_KEY) || "";
}

export function setToken(token: string) {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

/** Session-only cached op password (cleared on logout). */
export function getOpPassword(): string {
  return sessionStorage.getItem(OP_KEY) || "";
}

export function setOpPassword(pw: string) {
  if (pw) sessionStorage.setItem(OP_KEY, pw);
  else sessionStorage.removeItem(OP_KEY);
}

export function clearSession() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(OP_KEY);
}

export type Json = Record<string, unknown>;

export async function publicApi(
  path: string,
  opts: RequestInit & { json?: Json } = {},
): Promise<Json> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as Record<string, string> | undefined),
  };
  const res = await fetch(`${getApiBase()}${path}`, {
    ...opts,
    headers,
    body: opts.json ? JSON.stringify(opts.json) : opts.body,
  });
  const data = (await res.json().catch(() => ({}))) as Json;
  if (!res.ok || data.ok === false) {
    const err = new Error(
      String(data.message || data.error || `HTTP ${res.status}`),
    ) as Error & { data?: Json; status?: number };
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

async function adminFetch(
  path: string,
  opts: RequestInit & { json?: Json; opPassword?: string } = {},
): Promise<Json> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as Record<string, string> | undefined),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const op = opts.opPassword ?? getOpPassword();
  if (op) headers["X-Admin-Op-Password"] = op;

  const res = await fetch(`${getApiBase()}${path}`, {
    ...opts,
    headers,
    body: opts.json ? JSON.stringify(opts.json) : opts.body,
  });
  const data = (await res.json().catch(() => ({}))) as Json;
  if (!res.ok || data.ok === false) {
    const err = new Error(
      String(data.message || data.error || `HTTP ${res.status}`),
    ) as Error & { data?: Json; status?: number };
    err.data = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

/** Authenticated API; auto-prompts for op password when required. */
export async function adminApi(
  path: string,
  opts: RequestInit & { json?: Json; opPassword?: string } = {},
): Promise<Json> {
  try {
    return await adminFetch(path, opts);
  } catch (e) {
    const err = e as Error & { data?: Json };
    if (err.data?.error === "op_password_required") {
      const pw = window.prompt("此操作需要操作密码");
      if (!pw) throw e;
      setOpPassword(pw);
      return await adminFetch(path, opts);
    }
    throw e;
  }
}
